<template>
    <div class="load-mask-parent" style="overflow-x: auto;">
        <component-title
            :config="config">
        </component-title>
        <div :class="loadMask"></div>
        <data-table
            :my-ajax="dataTableAjax"
            :dt-id="gridId"
            :row-id="'product_id'"
            :columns="columns"
            :column-defs="columnDefs()"
            :refresh="DTRefresh"
            :refresh-view-with-preserve-paging="DTRefreshWithPreservePaging"
            on-click-element=".row-details"
            :on-click-element-event="onClickEvent"
            :on-page-change="onPageChange"
            :sort="defaultSort.length > 0"
            :order="defaultSort"
            :page-length="pagination"
        ></data-table>

        <div class="row">
            <div class="col-lg-12">
                <template v-for="btn in config.actionButtons">
                    <ActionComponent
                        :active="btn.active"
                        :config="btn.config"
                        :bind-events="btn.bindEvents"
                        :emit-events="btn.emitEvents || {}"
                        :event-bus="eventBus"
                        :route-name="routeName"
                        :title="btn.title"
                        :name="btn.name"
                        :state="state"
                    >
                    </ActionComponent>
                </template>
                <span>{{ t('Pages on side') }}:</span>
                <select v-model="pagination" class="pagination" @change="paginationChange">
                    <option v-for="_pagination in [10, 50, 100, 300, 500]" v-bind:value="_pagination">{{
                            _pagination
                        }}
                    </option>
                </select>
            </div>
        </div>
    </div>
</template>
<script>

import Vue from "vue";
import DataTable from "./DataTable";
import FormExport from "./FormExport";
import ComponentTitle from "./ComponentTitle";
import ColumnVisitor from "../services/product/columnVisitor";
import ActionComponent from "./grid/ActionComponent";
import eventManagerService from "../services/EventManager";
import ValueChecker from "../services/ValueChecker";

export default {
    components: {ActionComponent, DataTable, FormExport, ComponentTitle},
    props: {
        config: {
            Type: Object,
            default: function () {
                return {
                    actionButtons: [],
                    isDumpActive: false,
                }
            }
        },
        eventBus: {
            Type: Object,
        },
        index: {
            Type: Number
        },
        moduleConfig: {
            Type: Object
        },
        routeName: {
            Type: String,
            require: true,
        },
        rowIndex: {
            Type: Number,
        },
    },
    computed: {
        gridId() {
            return this.config.index + '_' + this.index + (this.rowIndex !== null ? ('_' + this.rowIndex) : '');
        }
    },
    data() {
        return {
            storage: {},
            columns: [],
            DTRefresh: false,
            DTRefreshWithPreservePaging: false,
            internalLoadEvent: null,
            internalCountEvent: null,
            internalScrollEvent: null,
            pagination: null,
            defaultSort: [],
            calculatePagination: true,
            events: [],
            moduleIndex: null,
            responseData: {
                data: [],
                recordsTotal: null,
                recordsFiltered: null,
            },
            firstLoading: false,
            mountedHeadersComponents: [],
            loadMask: '',
            state: {},
        };
    },
    beforeDestroy() {
        this.unmountEvents();
    },
    mounted() {
        this.$root.$once('CustomModule:ready', (customModuleEventName, moduleIndex) => {
            this.moduleIndex = moduleIndex;
            this.events.push('CustomModule:ready');
            this.events.push(customModuleEventName);

            this.$root.$on(customModuleEventName, (moduleConfig, state) => {
                this.mountEvents();
                this.init();
            });
        });

    },
    methods:
        {
            columnDefs() {
                return ColumnVisitor.visit(this.columns, this.moduleConfig.embeddedModules);
            },
            findActionByName(name) {
                return (this.config.actions || []).find(v => {
                    return v.name === name;
                });
            },

            afterLoadEvent(config, data) {
                this.responseData.data = data.data.data;
                if (null === this.responseData.recordsTotal) {
                    this.responseData.recordsTotal = data.recordsTotal;
                    this.responseData.recordsFiltered = data.recordsFiltered;
                }
                config.callback(this.responseData);
                this.mountComponentsForColumns(this.columns, this.responseData.data);
                this.$root.$emit('Grid:data-loaded', this.responseData.data, this.moduleConfig.moduleName);

                let eventManagerService = this.eventBus.getEventManager();
                if (this.calculatePagination) {
                    eventManagerService.emitEvents(this.internalCountEvent, [{'state': config.state}]);
                }
                eventManagerService.emitEvents(this.internalScrollEvent, [{'state': config.state}]);
            },

            afterCountEvent(config, data) {
                this.responseData.recordsTotal = data.data.recordsTotal;
                this.responseData.recordsFiltered = data.data.recordsFiltered;
                config.callback(this.responseData);
                this.mountComponentsForColumns(this.columns, this.responseData.data);
                this.$root.$emit('Grid:data-loaded', this.responseData.data, this.moduleConfig.moduleName);

                let eventManagerService = this.eventBus.getEventManager();
                eventManagerService.emitEvents(this.internalScrollEvent, [{'state': config.state}]);
            },
            afterScrollEvent(config, data) {
                setTimeout(() => {
                    this.$root.$emit('data-table:makeScrollableTable');
                    this.mountComponentsForHeader(this.columns);
                }, 1000);
            },

            bindLoadEvent(callback) {
                if (null === this.internalLoadEvent) {
                    this.internalLoadEvent = this.config.emitEvents['load'] || [];

                    this.internalLoadEvent.push({
                        type: 'frontend',
                        config: {
                            eventName: `${this.config.index}:afterLoadEvent`,
                            callback: callback,
                            data: {
                                data: "1"
                            }
                        }
                    });
                }
            },

            bindCountEvent(callback) {
                if (null === this.internalCountEvent) {
                    this.internalCountEvent = this.config.emitEvents['count'] || [];

                    this.internalCountEvent.push({
                        type: 'frontend',
                        config: {
                            eventName: `${this.config.index}:afterCountEvent`,
                            callback: callback,
                            data: {
                                data: "1"
                            }
                        }
                    });
                }
            },

            bindScrollEvent(callback) {
                if (null === this.internalScrollEvent) {
                    this.internalScrollEvent = [];

                    this.internalScrollEvent.push({
                        type: 'frontend',
                        config: {
                            eventName: `${this.config.index}:afterScrollEvent`,
                            callback: callback,
                        }
                    });
                }
            },

            dataTableAjax(data, callback) {
                let eventManagerService = this.eventBus.getEventManager();
                let stack = [];

                if (undefined === this.config.emitEvents) {
                    this.setConfigEmitEvents(data, callback);
                }

                this.bindLoadEvent(callback);
                this.bindCountEvent(callback);
                this.bindScrollEvent(callback);

                this.firstLoading = true;
                this.loadMask = 'lmask';
                delete data.columns;
                delete data.search;
                data.length = data.limit = this.pagination;
                data.offset = data.start;
                data = Object.assign(this.state, data);
                this.state = data;
                stack.push({'state': this.state});

                eventManagerService.emitEvents(this.internalLoadEvent, stack);
                this.loadMask = '';
            },
            setConfigEmitEvents(data, callback) {
                this.config.emitEvents = {'load': [], 'count': []};
                this.setConfigEmitEventsLoad(data, callback);
                this.setConfigEmitEventsCount(data, callback);
            },
            setConfigEmitEventsLoad(data, callback) {
                let url = this.config.url || (this.routeName + '/dt-' + this.config.index);
                let method = 'get';

                this.config.emitEvents.load = [
                    {
                        "type": "backend",
                        "config": {
                            "url": url,
                            "data": {
                                "": "0.state"
                            },
                            "method": method
                        }
                    }
                ];
            },
            setConfigEmitEventsCount(data, callback) {
                let url = this.config.url || (this.routeName + '/count-' + this.config.index);
                let method = 'get';

                this.config.emitEvents.count = [
                    {
                        "type": "backend",
                        "config": {
                            "url": url,
                            "data": {
                                "": "0.state"
                            },
                            "method": method
                        }
                    }
                ];
            },
            dump() {
                this.$http.get(this.routeName + '/dump-' + this.config.index, {params: this.state}).then((response) => {
                    alert(this.$translate.text('Your report will be ready in \'Export data\' module'));
                })
            },
            init() {
                this.pagination = this.config.limit;
                this.columns = this.config.columns;
            },
            getComponentIndex() {
                return `${this.config.index}`;
            },
            defaultBindEvents() {
                let componentIndex = this.getComponentIndex();

                let bindEvents = [{
                    name: 'afterLoadEvent',
                    componentIndex: componentIndex,
                }, {
                    name: 'afterCountEvent',
                    componentIndex: componentIndex,
                }, {
                    name: 'setState',
                    componentIndex: componentIndex,
                }, {
                    name: 'afterScrollEvent',
                    componentIndex: componentIndex,
                }];

                if (this.config.bindEvents) {
                    let configBindEvents = [];

                    (this.config.bindEvents || []).forEach(e => {
                        configBindEvents.push(Object.assign({}, e));
                    });
                    bindEvents = bindEvents.concat(configBindEvents);
                }

                return bindEvents;
            },
            mountEvents() {
                let eventManagerService = this.$root.getEventManager();
                eventManagerService.bindEvents(this.defaultBindEvents(), this);
                this.$root.$on('CustomModule:grid-refresh', () => {
                    this.refreshViewWithAutoLoadOption();
                });

                this.$root.$on(`${this.moduleIndex}-ToggleFilterPanel:filter-change`, (filterName, filterValue, filter, filters, serializedFilters) => {
                    this.responseData.recordsTotal = null;
                    this.calculatePagination = true;
                    this.setState(serializedFilters);

                    this.refreshViewWithAutoLoadOption();
                });
                this.events.push(`${this.moduleIndex}-ToggleFilterPanel:filter-change`);

                this.$root.$on('AttributesComponent:attributes-change', (attributes, serializedAttributes) => {
                    this.responseData.recordsTotal = null;
                    this.calculatePagination = true;
                    this.setState(serializedAttributes);
                    this.refreshViewWithAutoLoadOption();
                });

                this.$root.$on('load-data-grid', (config, data, componentIndex) => {
                    if (this.config.index === componentIndex) {
                        this.loadData(componentIndex, data);
                    }
                });
            },
            unmountEvents() {
                let eventManagerService = this.$root.getEventManager();
                eventManagerService.unbindEvents(this.defaultBindEvents());

                this.events.forEach(e => {
                    this.eventBus.$off(e);
                });
            },
            onClickEvent: function (rowId, row, e, data) {
                let tr = $(e.target).closest('tr');
                let icon = tr.find('.row-details .glyphicon');
                if (row.child.isShown()) {
                    row.child.hide();
                    icon.addClass('glyphicon-chevron-down');
                    icon.removeClass('glyphicon-chevron-up')
                } else {
                    icon.addClass('glyphicon-chevron-up');
                    icon.removeClass('glyphicon-chevron-down')
                    row.child(() => {
                        return '<div class="loading-fragment"><div class="dataTables_processing panel panel-default">' + this.$translate.text('Processing') + '...</div></div>';
                    }).show();
                    let $el = $(row.child()[0]).find('div')[0];

                    this.$root.$emit('Grid:row-onclick', this.config.index, rowId, row, $el, data);
                }
            },
            onPageChange() {
                this.calculatePagination = false;
            },
            paginationChange() {
                this.calculatePagination = false;
            },
            getDefaultHeaderComponent(column) {
                return {
                    name: 'DefaultHeaderComponent',
                    config: {
                        name: column.name,
                        title: column.title,
                        i18nParams: column.i18nParams
                    }
                };
            },
            mountComponentsForHeader(columns) {
                let _this = this;
                columns.forEach((column, columnIndex) => {
                    column.headerComponents = column.headerComponents || [this.getDefaultHeaderComponent(column)];

                    column.headerComponents.forEach(function (c, i) {
                        require([`./grid/${c.name}.vue`], (r) => {
                            if (_this.mountedHeadersComponents.includes('header-' + _this.gridId + '-' + columnIndex)) {
                                return;
                            }

                            let TemplateLoader = Vue.extend(r);
                            let comp = new TemplateLoader({
                                propsData: {
                                    eventBus: _this.eventBus,
                                    columnIndex: columnIndex,
                                    name: column.name,
                                    columnIdentify: column.data,
                                    config: c.config || {},
                                    parentIndex: _this.config['index']
                                }
                            }).$mount();
                            let el = document.getElementById('header-' + _this.gridId + '-' + columnIndex);

                            if (!el) {
                                return;
                            }

                            el.innerHTML = '';
                            el.appendChild(comp.$el);
                            _this.mountedHeadersComponents.push('header-' + _this.gridId + '-' + columnIndex);
                        });
                    });
                });

            },
            mountComponentsForColumns(columns, rows) {
                let _this = this;
                columns.forEach((c, columnIndex) => {
                    if (!c.components || c.components.length === 0) {
                        return;
                    }

                    rows.forEach(function (row, rowIndex) {
                        require([`./grid/GridComponentFactory.vue`], (r) => {
                            let t = _this.storage[`components_${columnIndex}_${rowIndex}`];

                            if (t) {
                                t.$destroy();
                            }

                            let TemplateLoader = Vue.extend(r);

                            t = new TemplateLoader({
                                propsData: {
                                    column: c,
                                    state: _this.state,
                                    components: c.components,
                                    embeddedModules: _this.moduleConfig.embeddedModules,
                                    eventBus: _this.eventBus,
                                    row: row,
                                    routeName: _this.routeName,
                                    columnIndex: columnIndex,
                                    rowIndex: rowIndex
                                }
                            });
                            _this.storage[`components_${columnIndex}_${rowIndex}`] = t;

                            t.$mount(document.getElementById(`components_${columnIndex}_${rowIndex}`));
                        });
                    });
                });
            },
            refreshView() {
                this.DTRefresh = !this.DTRefresh;
            },
            loadEvent(config, data) {
                this.setState(data);
                this.refreshView();
            },
            refreshViewWithAutoLoadOption() {
                if ((!ValueChecker.isUndefinedOrNull(this.config['autoLoad']) && !this.config['autoLoad']) && !this.firstLoading) {
                    return;
                }

                this.refreshView();
            },
            refreshViewWithPreservePaging() {
                this.DTRefreshWithPreservePaging = !this.DTRefreshWithPreservePaging;
            },
            refreshRowEvent(config, data) {
                if (ValueChecker.isUndefinedOrNull(config['row'])) {
                    throw new TypeError(`Grid:refreshRowEvent config "row" not set`);
                }

                if (ValueChecker.isUndefinedOrNull(config['identityField'])) {
                    throw new TypeError(`Grid:refreshRowEvent config "identityField" not set`);
                }

                if (ValueChecker.isUndefinedOrNull(data['row'])) {
                    throw new TypeError(`Grid:refreshRowEvent data "row" not set`);
                }

                let index = this.findRowIndex(config['row'], config['identityField']);

                if (index === -1) {
                    return;
                }

                Object.assign(this.responseData.data[index], data['row']);
            },
            loadData() {
                this.refreshView();
            },
            dumpData(eventConfig) {
                if (eventConfig.componentIndex && this.config.index === eventConfig.componentIndex) {
                    this.dump();
                }
            },
            findRowIndex(row, identityField) {
                let searchValue = row[identityField];

                return this.responseData.data.findIndex(r => r[identityField] === searchValue);
            },
            setState(data) {
                this.state = Object.assign(this.state, data);
            }
        },
}

</script>

<style>
.pagination {
    border-color: #e9e9e9;
    margin-top: 0;
    margin-bottom: 5px;
    height: 40px;
}
</style>



// WEBPACK FOOTER //
// Grid.vue?7777b914